import type { ClientMsg, Item, Market, MarketListing } from "./types";
import { sameItem } from "./pile";
import { num } from "./numfmt";

/**
 * The Market panel: Grubb's market (2026-10-07: "add a market feature
 * Grubb. you can buy and sell items to other players"). It is shown while
 * the server sends a market in the state, which it does beside any depot
 * box, where it sits in the depot screen. It came from Market on Grubb's
 * menu too until 2026-10-19; since then he sends you to the depot.
 *
 * From the top: the gold Grubb is holding for you from what of yours has
 * sold, with Collect; the lots on sale, filtered by name and sorted, each
 * with Buy (and how many, for a lot of more than one) or, for your own,
 * Take back; and your depot's goods that may be sold, of which one picked
 * fills the form under them — how many, and the gold each — and List puts
 * it in Grubb's keeping. The goods were the bag's until 2026-10-17
 * ("Allow listing items in the depot, not from the inventory"), and what
 * is taken back goes to the depot too. A long press on a lot, or a
 * right-click, asks what it is (2026-10-17: "Allow long press on market
 * listing to look at the item"). You keep one lot of each thing with him
 * (2026-10-13), so listing what you already list adds to that lot at the
 * price you give, and the form says so. Lots are shown without their
 * sellers, but for "yours". Everything is the server's to allow or refuse,
 * and it says why when it refuses.
 *
 * Since 2026-10-24 a lot's price halves each day, falling every hour, and
 * under a gold each it sells in batches for a gold (see market.go): a row
 * says the gold each, or "1 gold for 3", with what was asked, and Buy
 * takes whole batches. A lot never falls under a gold for all of it.
 * Other sellers' lots of one thing at one price come as one row, and a buy
 * takes the oldest listed first. Adding to your own lot keeps its price,
 * so the form hides Gold each for it: take it back to price it again.
 *
 * The page's own pieces (the item drawings and names, the long press, and
 * the socket) come
 * in as deps, so the panel runs under node with a stand-in document in the
 * tests (see market_test.go's client half).
 */

export interface MarketDeps {
  send: (m: ClientMsg) => void;
  /** An item kind's name, singular. */
  nameOf: (it: { id: number; name?: string }) => string;
  /** The drawing of a pile. */
  icon: (it: Item) => HTMLElement;
  /** Run fire on a long press or right-click of el, but not of its buttons and boxes. */
  hold: (el: HTMLElement, fire: () => void) => void;
}

/** How the lots are ordered. */
export type MarketSort = "newest" | "name" | "cheapest" | "dearest";

export const MARKET_SORTS: [MarketSort, string][] = [
  ["newest", "Newest"],
  ["name", "Name"],
  ["cheapest", "Cheapest"],
  ["dearest", "Dearest"],
];

/** The plural the server's Phrase makes: an s, nothing after one, es after ss or ch. */
function plural(name: string): string {
  const of = name.indexOf(" of ");
  if (of > 0) return plural(name.slice(0, of)) + name.slice(of);
  return name.endsWith("ss") || name.endsWith("ch") ? name + "es" : name.endsWith("s") ? name : name + "s";
}

/** What one of a lot is worth now, to sort by. */
export function marketEach(l: MarketListing): number {
  return l.each ?? l.price / (l.batch || 1);
}

/** A lot's price in words: "4 gold each", or "1 gold for 3" in batches. */
export function marketPriceWords(l: MarketListing): string {
  return (l.batch ?? 1) > 1 ? `${num(l.price)} gold for ${num(l.batch!)}` : `${num(l.price)} gold each`;
}

/** How long until a lot's price next falls, in words: "in 12 minutes". */
export function marketNextWords(next: number | undefined, nowSecs: number): string {
  if (!next) return "";
  const mins = Math.max(1, Math.ceil((next - nowSecs) / 60));
  return mins >= 60 ? "in an hour" : `in ${mins} minute${mins === 1 ? "" : "s"}`;
}

/** A pile in words: "a sword", "3 arrows". */
export function marketPhrase(name: string, count: number): string {
  if (count > 1) return `${count} ${plural(name)}`;
  return /^[aeiou]/i.test(name) ? `an ${name}` : /^[A-Z]/.test(name) ? name : `a ${name}`;
}

/** The lots shown, given what is typed in the filter and the order picked. */
export function marketRows(listings: MarketListing[], nameOf: (it: { id: number; name?: string }) => string, filter: string, sort: MarketSort): MarketListing[] {
  const f = filter.trim().toLowerCase();
  const rows = listings.filter((l) => !f || nameOf(l.item).toLowerCase().includes(f));
  const byName = (a: MarketListing, b: MarketListing) => nameOf(a.item).localeCompare(nameOf(b.item));
  const order: Record<MarketSort, (a: MarketListing, b: MarketListing) => number> = {
    newest: (a, b) => b.at - a.at || b.id - a.id,
    name: (a, b) => byName(a, b) || marketEach(a) - marketEach(b) || a.id - b.id,
    cheapest: (a, b) => marketEach(a) - marketEach(b) || byName(a, b) || a.id - b.id,
    dearest: (a, b) => marketEach(b) - marketEach(a) || byName(a, b) || a.id - b.id,
  };
  return rows.sort(order[sort]);
}

/** The messages the panel sends. */
export const marketSell = (it: Item, count: number, price: number): ClientMsg => ({ t: "marketsell", id: it.id, owner: it.owner, var: it.var, count, price });
export const marketBuy = (id: number, count: number): ClientMsg => ({ t: "marketbuy", id, count });
export const marketCancel = (id: number): ClientMsg => ({ t: "marketcancel", id });
export const marketCollect = (): ClientMsg => ({ t: "marketcollect" });
export const marketLook = (id: number): ClientMsg => ({ t: "marketlook", id });

/** A whole number from an input, clamped to lo..hi, or lo. */
function wholeIn(v: string, lo: number, hi: number): number {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : lo;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

export class MarketPanel {
  private held = el("div", "markethead");
  private heldText = el("span", "marketheld");
  private collect = el("button", "marketbtn", "Collect");
  private filter = el("input", "marketfilter");
  private sort = el("select", "marketsort");
  private list = el("div", "marketlist");
  private mineText = el("div", "marketmine");
  private goods = el("div", "marketgoods");
  private form = el("div", "marketform");
  private formWhat = el("div", "marketformwhat");
  private count = el("input", "marketcount");
  private price = el("input", "marketprice");
  private priceLabel = el("label", undefined, "Gold each ");
  private listBtn = el("button", "marketbtn", "List");
  private market: Market | undefined;
  /** The good picked for the form, by kind and owner. */
  private picked: Item | undefined;
  private lastList = "@";
  private lastGoods = "@";

  constructor(
    root: HTMLElement,
    private deps: MarketDeps,
  ) {
    this.collect.type = "button";
    this.collect.title = "Put the gold he is holding for you in your depot, and list what has sold since you last collected";
    this.collect.onclick = () => this.deps.send(marketCollect());
    this.held.append(this.heldText, this.collect);

    const tools = el("div", "markettools");
    this.filter.type = "search";
    this.filter.placeholder = "Filter by name";
    this.filter.setAttribute("aria-label", "Filter the lots by name");
    this.filter.oninput = () => this.drawList(true);
    this.sort.setAttribute("aria-label", "Order the lots");
    for (const [value, label] of MARKET_SORTS) {
      const o = el("option", undefined, label);
      o.value = value;
      this.sort.append(o);
    }
    this.sort.onchange = () => this.drawList(true);
    tools.append(this.filter, this.sort);

    this.count.type = "number";
    this.count.min = "1";
    this.count.value = "1";
    this.count.setAttribute("aria-label", "How many to sell");
    this.price.type = "number";
    this.price.min = "1";
    this.price.value = "1";
    this.price.setAttribute("aria-label", "Gold each");
    this.listBtn.type = "button";
    this.listBtn.onclick = () => this.listPicked();
    const countLabel = el("label", undefined, "How many ");
    countLabel.append(this.count);
    this.priceLabel.append(this.price);
    this.form.append(this.formWhat, countLabel, this.priceLabel, this.listBtn);
    this.form.hidden = true;

    root.replaceChildren(this.held, tools, this.list, el("div", "marketsub", "Sell"), this.mineText, this.goods, this.form);
  }

  /** Draw the market the state carries; nothing is redrawn that has not changed. */
  show(m: Market | undefined) {
    this.market = m;
    if (!m) {
      this.lastList = this.lastGoods = "@";
      this.picked = undefined;
      return;
    }
    const held = m.held ?? 0;
    this.held.hidden = held <= 0;
    this.heldText.textContent = `${m.keeper} is holding ${num(held)} gold for you.`;
    this.mineText.textContent = `Your lots: ${m.mine} of ${m.max}, one of each thing. Pick something from your depot to leave with ${m.keeper}.`;
    this.drawList(false);
    this.drawGoods();
    // Your lot of the picked thing may have come, gone or changed.
    if (this.picked) this.pick(this.picked);
  }

  private drawList(force: boolean) {
    const m = this.market;
    if (!m) return;
    const key = JSON.stringify(m.listings);
    if (!force && key === this.lastList) return;
    this.lastList = key;
    const rows = marketRows(m.listings, this.deps.nameOf, this.filter.value, this.sort.value as MarketSort);
    this.list.replaceChildren();
    if (rows.length === 0) {
      this.list.append(el("div", "none", m.listings.length ? "Nothing on sale by that name." : `Nothing is on sale with ${m.keeper} yet.`));
      return;
    }
    for (const l of rows) {
      const row = el("div", l.mine ? "marketrow mine" : "marketrow");
      const what = marketPhrase(this.deps.nameOf(l.item), l.item.count);
      const words = el("span", "marketwhat");
      words.append(el("span", "marketname", what), el("span", "marketprice", ` ${marketPriceWords(l)}`));
      if (l.asked && (l.asked !== l.price || (l.batch ?? 1) > 1)) words.append(el("span", "marketasked", ` (asked ${num(l.asked)} each)`));
      // Other sellers' lots at one price are one row, the oldest sold first.
      if ((l.lots ?? 1) > 1) words.append(el("span", "marketseller", ` · ${l.lots} lots, oldest sold first`));
      if (l.mine) words.append(el("span", "marketseller", " · yours"));
      row.append(this.deps.icon(l.item), words);
      const falls = marketNextWords(l.next, Date.now() / 1000);
      row.title = `Long press or right-click to look at it.${falls ? ` The price halves each day; it next falls ${falls}.` : ""}`;
      this.deps.hold(row, () => this.deps.send(marketLook(l.id)));
      if (l.mine) {
        const back = el("button", "marketbtn", "Take back");
        back.type = "button";
        back.title = `Take ${what} back into your depot`;
        back.onclick = () => this.deps.send(marketCancel(l.id));
        row.append(back);
      } else {
        // Whole batches: the box counts in them, so 1 is one batch.
        const batch = Math.min(l.batch ?? 1, l.item.count);
        const batches = Math.ceil(l.item.count / batch);
        let n: HTMLInputElement | undefined;
        if (l.item.count > 1) {
          n = el("input", "marketcount");
          n.type = "number";
          n.min = String(batch);
          n.step = String(batch);
          n.max = String(l.item.count);
          n.value = String(batch);
          n.setAttribute("aria-label", batch > 1 ? `How many to buy, in batches of ${batch}` : "How many to buy");
          row.append(n);
        }
        const buy = el("button", "marketbtn", "Buy");
        buy.type = "button";
        buy.title = batch > 1 ? `Buy at ${num(l.price)} gold for ${batches > 1 ? `every ${batch}` : "the lot"}` : `Buy at ${num(l.price)} gold each`;
        buy.onclick = () => this.deps.send(marketBuy(l.id, n ? wholeIn(n.value, 1, l.item.count) : 1));
        row.append(buy);
      }
      this.list.append(row);
    }
  }

  private drawGoods() {
    const m = this.market!;
    const key = JSON.stringify(m.goods);
    if (key === this.lastGoods) return;
    this.lastGoods = key;
    this.goods.replaceChildren();
    // A pick that has left the depot is no pick.
    const p = this.picked;
    if (p && !m.goods.some((g) => sameItem(g, p))) this.pick(undefined);
    if (m.goods.length === 0) {
      this.goods.append(el("div", "none", "Nothing in your depot can be sold here."));
      return;
    }
    for (const g of m.goods) {
      const slot = el("button", "slot");
      slot.type = "button";
      slot.title = `Sell ${marketPhrase(this.deps.nameOf(g), g.count)} here`;
      slot.append(this.deps.icon(g));
      if (g.count > 1) slot.append(el("span", "count", num(g.count)));
      slot.onclick = () => this.pick(g);
      this.goods.append(slot);
    }
    if (this.picked) this.pick(m.goods.find((g) => sameItem(g, this.picked!)));
  }

  /** The form for a picked good, which the depot screen shows under its depot (main.ts). */
  get formElement(): HTMLElement {
    return this.form;
  }

  /** Filter the lots by name from outside, as the depot screen's one filter does. */
  setFilter(text: string) {
    if (this.filter.value === text) return;
    this.filter.value = text;
    this.drawList(true);
  }

  /** Fill the form for one of the depot's goods, or shut it. */
  pick(g: Item | undefined) {
    const was = this.picked;
    this.picked = g;
    this.form.hidden = !g;
    if (!g) return;
    const lot = this.ownLot(g);
    const name = this.deps.nameOf(g);
    // The cheapest anybody else asks for the same pile, to price against
    // (2026-10-17, the depot screen): the same make too, since a
    // dwarf-wrought piece is not priced by plain ones (2026-10-28).
    const others = this.market!.listings.filter((l) => !l.mine && sameItem(l.item, g));
    const low = others.reduce<MarketListing | undefined>((a, l) => (!a || marketEach(l) < marketEach(a) ? l : a), undefined);
    const cheapest = (low ? ` The cheapest on sale now is ${marketPriceWords(low)}.` : " Nobody else has any on sale.") + " Prices halve each day a lot is unsold.";
    this.formWhat.textContent = (lot
      ? `${name}: you have ${num(g.count)} in your depot, and ${marketPhrase(name, lot.item.count)} with ${this.market!.keeper} at ${marketPriceWords(lot)}. What you list joins that lot at its price; to price it again, take it back and list it anew.`
      : `${name}: you have ${num(g.count)} in your depot.`) + cheapest;
    this.listBtn.textContent = lot ? "Add" : "List";
    this.priceLabel.hidden = !!lot;
    this.count.max = String(g.count);
    const fresh = !was || !sameItem(was, g);
    if (fresh) {
      this.count.value = String(g.count);
      if (lot) this.price.value = String(lot.asked ?? lot.price);
    } else this.count.value = String(wholeIn(this.count.value, 1, g.count));
  }

  /** Your own lot of the same pile as g, if you have one with Grubb. */
  private ownLot(g: Item): MarketListing | undefined {
    return this.market?.listings.find((l) => l.mine && sameItem(l.item, g));
  }

  private listPicked() {
    const g = this.picked;
    if (!g) return;
    this.deps.send(marketSell(g, wholeIn(this.count.value, 1, g.count), wholeIn(this.price.value, 1, 1_000_000)));
  }
}
